import React, { useState, useEffect } from 'react';
import {
  Search, Filter, Eye, Check, X, FileText, Clock, Users,
  UserCheck, UserX, Mail, Download, ChevronDown, ChevronUp, Calendar,
  AlertCircle, Send, MessageSquare, ChevronLeft, ChevronRight,
  Loader, User, MapPinned, Video, Phone,
  Link
} from 'lucide-react';
import { useSelector } from 'react-redux';
import api from '../../../utils/Api';
import { useNavigate, useSearchParams } from 'react-router-dom';

const CandidateAvatar = ({ candidate }) => {
  const [error, setError] = useState(false);

  if (!candidate?.profile_picture || error) {
    return (
      <div className="w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-950/50 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-bold text-primary-600 dark:text-primary-400">
          {candidate?.first_name?.charAt(0)}{candidate?.last_name?.charAt(0)}
        </span>
      </div>
    );
  }

  return (
    <div className="w-8 h-8 rounded-lg overflow-hidden flex-shrink-0">
      <img
        src={candidate.profile_picture}
        alt={`${candidate.first_name} ${candidate.last_name}`}
        className="w-full h-full object-cover"
        onError={() => setError(true)}
      />
    </div>
  );
};

const LargeCandidateAvatar = ({ candidate }) => {
  const [error, setError] = useState(false);

  if (!candidate?.profile_picture || error) {
    return (
      <div className="w-16 h-16 rounded-2xl bg-primary-100 dark:bg-primary-950/30 flex items-center justify-center">
        <span className="text-2xl font-bold text-primary-600 dark:text-primary-500">
          {candidate?.first_name?.charAt(0)}{candidate?.last_name?.charAt(0)}
        </span>
      </div>
    );
  }

  return (
    <div className="w-16 h-16 rounded-2xl overflow-hidden">
      <img
        src={candidate.profile_picture}
        alt={`${candidate.first_name} ${candidate.last_name}`}
        className="w-full h-full object-cover"
        onError={() => setError(true)}
      />
    </div>
  );
};

const CompanyApplications = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [actionConfirm, setActionConfirm] = useState({ id: null, action: null });
  const [showCVModal, setShowCVModal] = useState(false);



  // Appointment Modal
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentCandidate, setAppointmentCandidate] = useState(null);
  const [appointmentData, setAppointmentData] = useState({
    scheduledAt: '',
    location: '',
    meetingLink: '',
    notes: '',
    interviewType: 'in-person'
  });
  const [submittingAppointment, setSubmittingAppointment] = useState(false);
  const [appointmentError, setAppointmentError] = useState(null);

  const [filters, setFilters] = useState({
    status: '',
    jobOffer: '', // Can be jobOfferId or missionId
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('section') || 'jobs'; // Default to 'jobs'

  const handleTabChange = (tab) => {
    setSearchParams({ section: tab });
    setFilters(prev => ({ ...prev, jobOffer: '' }));
  };

  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    accepted: 0,
    rejected: 0
  });

  // ESC key handlers
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        if (showCVModal) closeCVModal();
        if (showAppointmentModal) resetAppointmentModal();
      }
    };

    if (showCVModal || showAppointmentModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showCVModal, showAppointmentModal]);

  const fetchApplications = async (page = 1) => {
    try {
      setLoading(true);
      const [jobAppsRes, missionAppsRes] = await Promise.all([
        api.get('/application/'),
        api.get('/mission-application/')
      ]);

      const jobApps = jobAppsRes.data.map(app => ({ ...app, type: 'job' }));
      const missionApps = missionAppsRes.data.map(app => ({ ...app, type: 'mission' }));

      const allApplications = [...jobApps, ...missionApps];

      // Filter by Active Tab
      const tabApps = allApplications.filter(app =>
        activeTab === 'jobs' ? app.type === 'job' : app.type === 'mission'
      );

      let filteredApplications = [...tabApps].sort((a, b) =>
        new Date(b.createdAt) - new Date(a.createdAt)
      );

      if (searchTerm) {
        filteredApplications = filteredApplications.filter(app =>
          app.candidate?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.candidate?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.jobOffer?.title?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          app.mission?.title?.title?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }

      if (filters.status) {
        filteredApplications = filteredApplications.filter(app => app.status === filters.status);
      }

      if (filters.jobOffer) {
        filteredApplications = filteredApplications.filter(app =>
          (app.jobOffer?._id === filters.jobOffer) || (app.mission?._id === filters.jobOffer)
        );
      }

      setStats({
        total: tabApps.length,
        pending: tabApps.filter(app => app.status === 'pending').length,
        accepted: tabApps.filter(app => app.status === 'accepted').length,
        rejected: tabApps.filter(app => app.status === 'rejected').length
      });

      const limit = 20;
      const total = filteredApplications.length;
      const pages = Math.ceil(total / limit);
      const start = (page - 1) * limit;
      const paginatedApplications = filteredApplications.slice(start, start + limit);

      setApplications(paginatedApplications);
      setPagination({ current: page, pages, total });

      setSelectedApplication(prev => {
        if (prev) {
          const updatedSelected = paginatedApplications.find(app => app._id === prev._id);
          if (updatedSelected) return updatedSelected;
        }
        return paginatedApplications.length > 0 ? paginatedApplications[0] : null;
      });

    } catch (err) {
      setError('Erreur lors du chargement des candidatures');
      console.error('Error fetching applications:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm !== '' || filters.status || filters.jobOffer) {
        fetchApplications(1);
      } else {
        fetchApplications(pagination.current);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, filters, activeTab]);

  const handleStatusChange = async (applicationId, newStatus) => {
    try {
      const app = applications.find(a => a._id === applicationId);
      const endpoint = app?.type === 'mission'
        ? `/mission-application/${applicationId}`
        : `/application/${applicationId}`;

      await api.put(endpoint, { status: newStatus });
      fetchApplications(pagination.current);
      setActionConfirm({ id: null, action: null });
    } catch (err) {
      console.error('Error updating application status:', err);
      setError('Erreur lors de la mise à jour du statut');
    }
  };

  const handleSendMessage = async (candidateId) => {
    const userId = typeof candidateId === 'object' ? candidateId._id : candidateId;

    if (!userId) {
      alert("Impossible de contacter ce candidat : ID manquant.");
      return;
    }

    try {
      const response = await api.post('/chat/conversations', {
        participantId: userId
      });

      const conversationId = response.data.data?._id || response.data._id;
      navigate('/dashboard/messages', {
        state: { conversationId }
      });

    } catch (error) {
      console.error('Error creating conversation:', error);
      alert('Erreur lors de la création de la conversation');
    }
  };

  // CV Modal State

  const [cvBlobUrl, setCvBlobUrl] = useState(null);

  // CV Logic
  const handleViewCV = async (cvUrl) => {
    if (!cvUrl) return;

    try {
      let folderName = 'cv';
      let fileName = cvUrl;

      if (cvUrl.includes('/')) {
        const parts = cvUrl.split('/');
        fileName = parts.pop();
        if (cvUrl.startsWith('http')) {
          const urlParts = cvUrl.split('/');
          folderName = urlParts[urlParts.length - 2];
        } else {
          if (parts.length > 0) folderName = parts[parts.length - 1];
        }
      }

      const response = await api.get(`/files/${folderName}/${fileName}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      setCvBlobUrl(url);
      setShowCVModal(true);
    } catch (error) {
      console.error("Error viewing CV:", error);
      alert("Erreur lors de l'ouverture du CV");
    }
  };

  const closeCVModal = () => {
    setShowCVModal(false);
    if (cvBlobUrl) {
      window.URL.revokeObjectURL(cvBlobUrl);
      setCvBlobUrl(null);
    }
  };

  const handleDownloadCV = async (cvUrl) => {
    if (!cvUrl) return;
    try {
      let folderName = 'cv';
      let fileName = cvUrl;

      if (cvUrl.includes('/')) {
        const parts = cvUrl.split('/');
        fileName = parts.pop();
        if (cvUrl.startsWith('http')) {
          const urlParts = cvUrl.split('/');
          folderName = urlParts[urlParts.length - 2];
        } else {
          if (parts.length > 0) folderName = parts[parts.length - 1];
        }
      }

      const response = await api.get(`/files/${folderName}/${fileName}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: response.headers['content-type'] });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading CV:', err);
      alert("Erreur lors du téléchargement du CV");
    }
  };

  // Appointment handlers
  const handleOpenAppointmentModal = (application) => {
    setAppointmentCandidate(application);
    setShowAppointmentModal(true);
  };

  const handleAppointmentChange = (field, value) => {
    setAppointmentData(prev => ({ ...prev, [field]: value }));
    if (appointmentError) setAppointmentError(null);
  };

  const validateAppointmentForm = () => {
    if (!appointmentData.scheduledAt) {
      setAppointmentError('La date et l\'heure sont requises');
      return false;
    }

    const selectedDate = new Date(appointmentData.scheduledAt);
    const now = new Date();

    if (selectedDate <= now) {
      setAppointmentError('La date doit être dans le futur');
      return false;
    }

    if (appointmentData.interviewType === 'in-person' && !appointmentData.location.trim()) {
      setAppointmentError('Le lieu est requis pour un entretien en personne');
      return false;
    }

    if (appointmentData.interviewType === 'remote' && !appointmentData.meetingLink.trim()) {
      setAppointmentError('Le lien de réunion est requis pour un entretien en visio');
      return false;
    }

    return true;
  };

  const handleScheduleAppointment = async (e) => {
    e.preventDefault();
    setAppointmentError(null);

    if (!validateAppointmentForm()) return;

    try {
      setSubmittingAppointment(true);

      const payload = {
        candidate: appointmentCandidate.candidate._id,
        scheduledAt: appointmentData.scheduledAt,
        interviewer: user._id,
        status: 'scheduled',
        context: appointmentCandidate._id,
        contextModel: appointmentCandidate.type === 'mission' ? 'MissionApplication' : 'Application'
      };

      if (appointmentData.interviewType === 'in-person') {
        payload.location = appointmentData.location;
      } else if (appointmentData.interviewType === 'remote') {
        payload.meetingLink = appointmentData.meetingLink;
      }

      if (appointmentData.notes.trim()) {
        payload.notes = appointmentData.notes;
      }

      await api.post('/appointment', payload);

      resetAppointmentModal();
      fetchApplications(pagination.current);
      alert('Entretien planifié avec succès!');

    } catch (err) {
      console.error('Error scheduling appointment:', err);
      setAppointmentError(
        err.response?.data?.message || 'Erreur lors de la planification de l\'entretien'
      );
    } finally {
      setSubmittingAppointment(false);
    }
  };

  const resetAppointmentModal = () => {
    setAppointmentData({
      scheduledAt: '',
      location: '',
      meetingLink: '',
      notes: '',
      interviewType: 'in-person'
    });
    setAppointmentError(null);
    setShowAppointmentModal(false);
    setAppointmentCandidate(null);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: {
        color: 'bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800',
        icon: Clock,
        text: 'En attente'
      },
      appointmentscheduled: {
        color: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
        icon: Calendar,
        text: 'RDV planifié'
      },
      accepted: {
        color: 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
        icon: UserCheck,
        text: 'Acceptée'
      },
      rejected: {
        color: 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-400 border-gray-200 dark:border-gray-700',
        icon: UserX,
        text: 'Rejetée'
      },
      interview_accepted: {
        color: 'bg-indigo-50 dark:bg-indigo-950/30 text-indigo-700 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
        icon: Check,
        text: 'Pour Entretien'
      },
      interview_pending: {
        color: 'bg-purple-50 dark:bg-purple-950/30 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800',
        icon: Calendar,
        text: 'Entretien Prévu'
      },
      interview_process: {
        color: 'bg-orange-50 dark:bg-orange-950/30 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-800',
        icon: Video,
        text: 'En Cours d\'Entretien'
      },
      validated: {
        color: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
        icon: UserCheck,
        text: 'Validée (Recruté)'
      },
      not_validated: {
        color: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800',
        icon: UserX,
        text: 'Non Validée'
      }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border ${config.color}`}>
        <Icon className="h-3 w-3" />
        {config.text}
      </span>
    );
  };

  const getUniquePosts = () => {
    const uniquePosts = [];
    const seenIds = new Set();

    // Filter apps based on active tab first
    const relevantApps = applications.filter(app =>
      activeTab === 'jobs' ? app.type === 'job' : app.type === 'mission'
    );
    // But wait, 'applications' state already contains filtered apps? 
    // No, 'applications' state holds the result of fetchApplications which puts PAGINATED, FILTERED apps.
    // So we can just iterate over 'applications' if we want currently visible ones, 
    // OR if we want ALL options we need access to 'allApplications' which is local to fetch function.
    // Actually, let's just loop through currently fetched applications for simplicity as filters usually narrow down.
    // However, a better UX is showing all available posts for the current tab even if not in current page, 
    // but we don't store 'allApplications' in state. 
    // Let's rely on what we have or accept that the filter options adapt to search results.
    // Re-reading fetchApplications: setApplications sets PAGINATED list.

    // Ideally we should persist 'allApplications' or all filtered apps before pagination to compute unique posts.
    // But for now let's use the 'applications' (paginated) which might be limited.
    // A better approach: The previous code iterated 'applications'.
    // Use the same approach.

    // To properly show dropdown options, we might need all matching the current tab. 
    // Since we don't have 'allApplications' in state, let's fix that or just use 'applications'.
    // Using 'applications' (paginated) leads to disappearing options when paging. 
    // Let's stick to what was there, but awareness of activeTab is implied if 'applications' is already filtered by fetch.
    // Yes, 'fetchApplications' filters by 'activeTab', so 'applications' state ONLY contains items of current tab.

    applications.forEach(app => {
      if (app.type === 'job' && app.jobOffer && !seenIds.has(app.jobOffer._id)) {
        uniquePosts.push({ id: app.jobOffer._id, title: app.jobOffer.title?.title || 'Offre sans titre', type: 'job' });
        seenIds.add(app.jobOffer._id);
      }
      if (app.type === 'mission' && app.mission && !seenIds.has(app.mission._id)) {
        uniquePosts.push({ id: app.mission._id, title: app.mission.title?.title || 'Mission sans titre', type: 'mission' });
        seenIds.add(app.mission._id);
      }
    });

    return uniquePosts;
  };

  const clearFilters = () => {
    setFilters({ status: '', jobOffer: '' });
    setSearchTerm('');
  };

  const getMinDateTime = () => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  };

  if (loading && applications.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/30 rounded-lg flex items-center justify-center">
                  <Users className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 dark:text-white">Candidatures</h1>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2 text-xs">
                <div className="px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <span className="text-gray-700 dark:text-gray-300">{stats.total} total</span>
                </div>
                <div className="px-2 py-1 bg-yellow-50 dark:bg-yellow-950/30 rounded-lg">
                  <span className="text-yellow-700 dark:text-yellow-400">{stats.pending} en attente</span>
                </div>
                <div className="px-2 py-1 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg">
                  <span className="text-emerald-700 dark:text-emerald-400">{stats.accepted} acceptées</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <div className="relative w-80">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un candidat, poste..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
                />
              </div>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-2 rounded-lg transition-colors ${showFilters
                  ? 'bg-primary-600 text-white'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <Filter className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex px-6 border-b border-gray-200 dark:border-gray-800">
          <button
            onClick={() => handleTabChange('jobs')}
            className={`pb-3 pt-2 text-sm font-medium border-b-2 transition-colors mr-6 ${activeTab === 'jobs'
              ? 'border-primary-600 text-primary-600 dark:text-primary-400 dark:border-primary-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
          >
            Offres d'emploi
          </button>
          <button
            onClick={() => handleTabChange('missions')}
            className={`pb-3 pt-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'missions'
              ? 'border-purple-600 text-purple-600 dark:text-purple-400 dark:border-purple-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
          >
            Missions
          </button>
        </div>

        {showFilters && (
          <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2 flex-wrap">
              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="w-40 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Tous les statuts</option>
                <option value="pending">En attente</option>
                <option value="accepted">Acceptées</option>
                <option value="rejected">Rejetées</option>
              </select>
              <select
                value={filters.jobOffer}
                onChange={(e) => setFilters(prev => ({ ...prev, jobOffer: e.target.value }))}
                className="w-48 px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">{activeTab === 'jobs' ? 'Toutes les offres' : 'Toutes les missions'}</option>
                {getUniquePosts().map((post) => (
                  <option key={post.id} value={post.id}>
                    {post.title}
                  </option>
                ))}
              </select>
              <button
                onClick={clearFilters}
                className="px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1.5"
              >
                <X className="h-3.5 w-3.5" />
                Effacer
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 3-Column Layout */}
      <div className={`flex overflow-hidden ${showFilters ? 'h-[calc(100vh-180px)]' : 'h-[calc(100vh-120px)]'}`}>
        {/* Left - List */}
        <div className="w-80 border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 flex flex-col">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between flex-shrink-0">
            <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
              {applications.length} candidatures {activeTab === 'jobs' ? "(Offres)" : "(Missions)"}
            </span>
          </div>

          <div className="flex-1 overflow-y-auto p-3">
            {applications.length === 0 ? (
              <div className="text-center py-8">
                <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-700 mx-auto mb-2" />
                <p className="text-xs text-gray-500">Aucune candidature</p>
              </div>
            ) : (
              <div className="space-y-2">
                {applications.map((app) => (
                  <button
                    key={app._id}
                    onClick={() => setSelectedApplication(app)}
                    className={`w-full p-3 rounded-lg transition-all text-left ${selectedApplication?._id === app._id
                      ? 'bg-primary-50 dark:bg-primary-950/30 border border-primary-200 dark:border-primary-800'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent'
                      }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <CandidateAvatar candidate={app.candidate} />

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                          {app.candidate?.first_name} {app.candidate?.last_name}
                        </h4>
                      </div>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate mb-2">
                      {app.type === 'mission' ? <span className="text-purple-600 font-medium">[Mission] </span> : ''}
                      {app.jobOffer?.title?.title || app.mission?.title?.title || 'Sans titre'}
                    </p>
                    <div className="flex items-center justify-between">
                      {getStatusBadge(app.status)}
                      <span className="text-xs text-gray-500">{formatDate(app.appliedAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {pagination.pages > 1 && (
            <div className="p-3 border-t border-gray-200 dark:border-gray-800 flex-shrink-0">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2">
                <span>Page {pagination.current}/{pagination.pages}</span>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => fetchApplications(pagination.current - 1)}
                  disabled={pagination.current === 1}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Préc.
                </button>
                <button
                  onClick={() => fetchApplications(pagination.current + 1)}
                  disabled={pagination.current === pagination.pages}
                  className="flex-1 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-1"
                >
                  Suiv.
                  <ChevronRight className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Center - Details */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-950">
          {selectedApplication ? (
            <div className="p-6 max-w-4xl mx-auto">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-6 mb-4">
                <div className="flex items-start gap-4">
                  <LargeCandidateAvatar candidate={selectedApplication.candidate} />


                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
                      {selectedApplication.candidate?.first_name} {selectedApplication.candidate?.last_name}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-3">
                      Candidature pour : &nbsp;
                      <a href={selectedApplication.type === 'mission' ? `/dashboard/missions/${selectedApplication.mission?._id}` : `/dashboard/job-offers/${selectedApplication.jobOffer?._id}`} className="hover:underline">
                        {selectedApplication.type === 'mission' ? <span className="text-purple-600 font-medium">[Mission] </span> : ''}
                        {selectedApplication.jobOffer?.title?.title || selectedApplication.mission?.title?.title || 'Sans titre'}
                      </a>

                    </p>

                    <div className="flex flex-wrap gap-2">
                      {getStatusBadge(selectedApplication.status)}
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium rounded-lg">
                        <Clock className="h-3 w-3" />
                        {formatDate(selectedApplication.appliedAt)}
                      </span>
                      {selectedApplication.cv && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 text-xs font-medium rounded-lg">
                          <FileText className="h-3 w-3" />
                          CV fourni
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-800">
                  {selectedApplication.candidate?.user_id?.email && !selectedApplication.candidate.user_id.email.includes('*') ? (
                    <a
                      href={`mailto:${selectedApplication.candidate?.user_id.email}`}
                      className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-primary-600 dark:hover:text-primary-500"
                    >
                      <Mail className="h-4 w-4" />
                      {selectedApplication.candidate?.user_id.email}
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-not-allowed">
                      <Mail className="h-4 w-4" />
                      Email masqué (Validation requise)
                    </span>
                  )}
                </div>
              </div>

              {selectedApplication.coverLetter && selectedApplication.coverLetterType === 'text' && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                    Lettre de motivation
                  </h3>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {selectedApplication.coverLetter}
                    </p>
                  </div>
                </div>
              )}

              {/* Questionnaire Answers */}
              {selectedApplication.answers && selectedApplication.answers.length > 0 && (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 p-5 mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                    Questions de pré-sélection
                  </h3>
                  <div className="space-y-4">
                    {selectedApplication.answers.map((answer, index) => (
                      <div key={index} className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <p className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                          {answer.questionText}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-300">
                          {typeof answer.answer === 'boolean'
                            ? (answer.answer ? 'Oui' : 'Non')
                            : answer.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="h-full flex items-center justify-center">
              <div className="text-center">
                <User className="h-16 w-16 text-gray-300 dark:text-gray-700 mx-auto mb-4" />
                <p className="text-gray-500 dark:text-gray-500">Sélectionnez une candidature</p>
              </div>
            </div>
          )}
        </div>

        {/* Right - Actions */}
        <div className="w-80 border-l border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-y-auto">
          {selectedApplication ? (
            <div className="p-4 space-y-4">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Actions rapides</h3>

              {/* Status Actions - Workflow Buttons */}
              {selectedApplication.status !== 'validated' && selectedApplication.status !== 'rejected' && selectedApplication.status !== 'not_validated' && (
                <div className="space-y-2">

                  {/* JOB OFFER WORKFLOW (Standard) */}
                  {selectedApplication.type === 'job' && (
                    <>
                      {(selectedApplication.status === 'pending' || selectedApplication.status === 'accepted') && (
                        <button
                          onClick={() => handleOpenAppointmentModal(selectedApplication)}
                          className="w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                        >
                          <Calendar className="h-4 w-4" />
                          Planifier un entretien
                        </button>
                      )}

                      {selectedApplication.status === 'pending' && (
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'accepted' })}
                            className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Check className="h-4 w-4" />
                            Accepter
                          </button>
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'rejected' })}
                            className="px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <X className="h-4 w-4" />
                            Rejeter
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {/* MISSION WORKFLOW (Admin Mediated) */}
                  {selectedApplication.type === 'mission' && (
                    <>
                      {/* PENDING -> INTERVIEW ACCEPTED */}
                      {selectedApplication.status === 'pending' && (
                        <>
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'interview_accepted' })}
                            className="w-full px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Check className="h-4 w-4" />
                            Demander un Entretien (Admin)
                          </button>
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'rejected' })}
                            className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <X className="h-4 w-4" />
                            Rejeter
                          </button>
                        </>
                      )}

                      {/* INTERVIEW ACCEPTED -> Waiting for Admin */}
                      {selectedApplication.status === 'interview_accepted' && (
                        <div className="p-4 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-xl text-center">
                          <Clock className="h-6 w-6 text-orange-500 mx-auto mb-2" />
                          <p className="text-sm font-medium text-orange-800 dark:text-orange-300">
                            En attente de planification
                          </p>
                          <p className="text-xs text-orange-600 dark:text-orange-400 mt-1">
                            L'administrateur va programmer l'entretien vidéo.
                          </p>
                        </div>
                      )}

                      {/* INTERVIEW PENDING -> INTERVIEW PROCESS */}
                      {selectedApplication.status === 'interview_pending' && (
                        <div className="space-y-4">
                          <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">Entretien Planifié</p>
                            <div className="flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400 mb-2">
                              <Calendar className="h-3 w-3" />
                              {formatDate(selectedApplication.interviewDate)}
                            </div>
                            {selectedApplication.interviewLink && ((
                              <a
                                href={selectedApplication.interviewLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1 text-xs font-bold text-blue-700 underline"
                              >
                                <Video className="h-3 w-3" /> Lien Visio
                              </a>
                            ))}
                          </div>

                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'interview_process' })}
                            className="w-full px-4 py-2.5 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <Video className="h-4 w-4" />
                            Démarrer l'Entretien
                          </button>
                        </div>
                      )}

                      {/* INTERVIEW PROCESS -> VALIDATED / NOT VALIDATED */}
                      {selectedApplication.status === 'interview_process' && (
                        <>
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'validated' })}
                            className="w-full px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <UserCheck className="h-4 w-4" />
                            Valider (Recruter)
                          </button>
                          <button
                            onClick={() => setActionConfirm({ id: selectedApplication._id, action: 'not_validated' })}
                            className="w-full px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                          >
                            <UserX className="h-4 w-4" />
                            Ne pas valider
                          </button>
                        </>
                      )}
                    </>
                  )}

                </div>
              )}

              {/* Special handling for Job Apps to keep simple flow if needed, or if I want to use standard flow for them too? 
                  "Refining Recruiter Applications Display" was a previous task.
                  Let's assume generic "Accepter/Rejeter" for Jobs is still desired unless specified.
                  But for now, I'll apply the new workflow to Missions ONLY.
              */}




              {actionConfirm.id === selectedApplication._id && (
                <div className={`p-4 rounded-lg border-2 mt-4 ${actionConfirm.action === 'accepted' || actionConfirm.action === 'validated' || actionConfirm.action === 'interview_accepted'
                  ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
                  : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700'
                  }`}>
                  <div className="flex items-start gap-3">
                    <AlertCircle className={`h-5 w-5 mt-0.5 ${actionConfirm.action === 'accepted'
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-600 dark:text-gray-400'
                      }`} />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-900 dark:text-white mb-2">
                        Confirmer le statut : {actionConfirm.action} ?
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleStatusChange(selectedApplication._id, actionConfirm.action)}
                          className={`px-3 py-1.5 text-xs font-medium text-white rounded-lg bg-gray-900 hover:bg-gray-800`}
                        >
                          Confirmer
                        </button>
                        <button
                          onClick={() => setActionConfirm({ id: null, action: null })}
                          className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg"
                        >
                          Annuler
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CV Actions */}
              <div className="space-y-2 pt-4">
                {selectedApplication.cv && (
                  <>
                    <button
                      onClick={() => handleViewCV(selectedApplication.cv)}
                      className="w-full px-4 py-2.5 bg-primary-600 hover:bg-primary-700 dark:bg-primary-400 dark:hover:bg-primary-500 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Eye className="h-4 w-4" />
                      Voir le CV
                    </button>
                    <button
                      onClick={() => handleDownloadCV(selectedApplication.cv)}
                      className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                    >
                      <Download className="h-4 w-4" />
                      Télécharger CV
                    </button>
                  </>
                )}
                <button
                  onClick={() => navigate(`/dashboard/applications/${activeTab}/${selectedApplication._id}`)}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <Eye className="h-4 w-4" />
                  Voir les détails
                </button>
                <button
                  onClick={() => handleSendMessage(selectedApplication.candidate?.user_id?._id || selectedApplication.candidate?.user_id)}
                  className="w-full px-4 py-2.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 text-sm"
                >
                  <MessageSquare className="h-4 w-4" />
                  Contacter le candidat
                </button>

              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-800">
                <h4 className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-3">Informations</h4>
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Candidature</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      {formatDate(selectedApplication.appliedAt)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Statut</span>
                    {getStatusBadge(selectedApplication.status)}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="h-full flex items-center justify-center p-4">
              <p className="text-sm text-gray-500 text-center">Sélectionnez une candidature</p>
            </div>
          )}
        </div>
      </div>


      {/* CV Modal */}
      {
        showCVModal && cvBlobUrl && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={closeCVModal}
          >
            <div
              className="relative w-full max-w-6xl h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">CV du candidat</h3>
                    <p className="text-xs text-gray-500">ESC pour fermer</p>
                  </div>
                </div>
                <button
                  onClick={closeCVModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>
              <div className="w-full h-[calc(100%-72px)]">
                <iframe
                  src={`${cvBlobUrl}#view=FitH`}
                  className="w-full h-full"
                  title="CV"
                />
              </div>
            </div>
          </div>
        )
      }

      {/* Appointment Modal */}
      {
        showAppointmentModal && appointmentCandidate && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={resetAppointmentModal}
          >
            <div
              className="relative w-full max-w-2xl max-h-[90vh] flex flex-col bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Planifier un Entretien</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {appointmentCandidate.candidate?.first_name} {appointmentCandidate.candidate?.last_name}
                    </p>
                  </div>
                </div>
                <button
                  onClick={resetAppointmentModal}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              <form onSubmit={handleScheduleAppointment} className="p-6 space-y-5 overflow-y-auto">
                {appointmentError && (
                  <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-300">Erreur</p>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">{appointmentError}</p>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Date et Heure <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="datetime-local"
                    required
                    value={appointmentData.scheduledAt}
                    onChange={(e) => handleAppointmentChange('scheduledAt', e.target.value)}
                    min={getMinDateTime()}
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Type d'Entretien <span className="text-red-500">*</span>
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      type="button"
                      onClick={() => handleAppointmentChange('interviewType', 'in-person')}
                      className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'in-person'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                      <MapPinned className="h-5 w-5" />
                      <span className="text-xs font-medium">En personne</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAppointmentChange('interviewType', 'remote')}
                      className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'remote'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                      <Video className="h-5 w-5" />
                      <span className="text-xs font-medium">Visio</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleAppointmentChange('interviewType', 'phone')}
                      className={`flex flex-col items-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${appointmentData.interviewType === 'phone'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300'
                        : 'border-gray-300 dark:border-gray-600 hover:border-primary-300 dark:hover:border-primary-700 text-gray-700 dark:text-gray-300'
                        }`}
                    >
                      <Phone className="h-5 w-5" />
                      <span className="text-xs font-medium">Téléphone</span>
                    </button>
                  </div>
                </div>

                {appointmentData.interviewType === 'in-person' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Lieu <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={appointmentData.location}
                      onChange={(e) => handleAppointmentChange('location', e.target.value)}
                      placeholder="Adresse du lieu de rendez-vous"
                      className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                )}

                {appointmentData.interviewType === 'remote' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Lien de Réunion <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      required
                      value={appointmentData.meetingLink}
                      onChange={(e) => handleAppointmentChange('meetingLink', e.target.value)}
                      placeholder="https://meet.google.com/xxx-xxxx-xxx"
                      className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Notes (optionnel)
                  </label>
                  <textarea
                    value={appointmentData.notes}
                    onChange={(e) => handleAppointmentChange('notes', e.target.value)}
                    rows={3}
                    placeholder="Ajouter des notes..."
                    className="w-full px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={resetAppointmentModal}
                    disabled={submittingAppointment}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submittingAppointment}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingAppointment ? (
                      <span className="inline-flex items-center gap-2">
                        <Clock className="h-4 w-4 animate-spin" />
                        Planification...
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        Planifier l'Entretien
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )
      }
    </div >
  );
};

export default CompanyApplications;
